import {
  Badge,
  Button,
  ButtonGroup,
  Dropdown,
  Form,
  InputGroup,
  Spinner,
  ToggleButton,
} from 'react-bootstrap';
import type { UrlListStatusFilter } from '../api/types';
import { formatFaNumber } from '../lib/utils';
import { IndexerIcon } from './IndexerIcon';

const SORT_OPTIONS = [
  { value: 'last_inspect', label: 'آخرین بازرسی' },
  { value: 'last_status_change', label: 'آخرین تغییر وضعیت' },
  { value: 'last_index', label: 'آخرین ایندکس' },
  { value: 'lastmod', label: 'lastmod نقشه' },
  { value: 'first_seen', label: 'اولین مشاهده' },
  { value: 'url', label: 'آدرس' },
] as const;

export function UrlListToolbar({
  searchInput,
  onSearchInputChange,
  onSearchSubmit,
  onSearchClear,
  sort,
  onSortChange,
  syncing,
  onSyncSitemap,
  busy,
  selectedCount,
  status,
  statusChangedCount,
  onStatusChange,
  onSendSelectedIndex,
  onInspectSelected,
  onCopySelected,
  onExportTxt,
  onSendNotIndexedPage,
  onInspectAllPage,
}: {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearchSubmit: () => void;
  onSearchClear: () => void;
  sort: string;
  onSortChange: (value: string) => void;
  syncing: boolean;
  onSyncSitemap: () => void;
  busy: boolean;
  selectedCount: number;
  status: UrlListStatusFilter;
  statusChangedCount?: number;
  onStatusChange: (status: UrlListStatusFilter) => void;
  onSendSelectedIndex: () => void;
  onInspectSelected: () => void;
  onCopySelected: () => void;
  onExportTxt: () => void;
  onSendNotIndexedPage: () => void;
  onInspectAllPage: () => void;
}) {
  const filters: { value: UrlListStatusFilter; label: string }[] = [
    { value: 'all', label: 'همه' },
    { value: 'indexed', label: 'ایندکس‌شده' },
    { value: 'not_indexed', label: 'ایندکس‌نیست' },
    { value: 'error', label: 'خطا' },
    { value: 'never_inspected', label: 'بدون بازرسی' },
    {
      value: 'status_changed',
      label:
        statusChangedCount != null
          ? `تغییر اخیر (${formatFaNumber(statusChangedCount)})`
          : 'تغییر اخیر',
    },
  ];

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <ButtonGroup size="sm">
          {filters.map((item) => (
            <ToggleButton
              key={item.value}
              id={`url-list-status-${item.value}`}
              type="radio"
              variant="outline-secondary"
              name="url-list-status"
              value={item.value}
              checked={status === item.value}
              onChange={() => onStatusChange(item.value)}
            >
              {item.label}
            </ToggleButton>
          ))}
        </ButtonGroup>

        {selectedCount > 0 ? (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge bg="primary" pill>
              {formatFaNumber(selectedCount)} انتخاب‌شده
            </Badge>
            <Button
              size="sm"
              variant="falcon-primary"
              onClick={onSendSelectedIndex}
            >
              ایندکس
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              disabled={busy}
              onClick={onInspectSelected}
            >
              بازرسی
            </Button>
          </div>
        ) : null}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2">
        <InputGroup size="sm" style={{ maxWidth: 320, minWidth: 180 }}>
          <InputGroup.Text>
            <IndexerIcon name="search" />
          </InputGroup.Text>
          <Form.Control
            placeholder="جستجو در URL…"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearchSubmit();
            }}
            aria-label="جستجو در URL"
          />
          {searchInput ? (
            <Button
              variant="outline-secondary"
              className="fs--2"
              onClick={onSearchClear}
              aria-label="پاک کردن جستجو"
            >
              پاک
            </Button>
          ) : null}
          <Button
            variant="falcon-primary"
            className="fs--2"
            onClick={onSearchSubmit}
            aria-label="اجرای جستجو"
          >
            جستجو
          </Button>
        </InputGroup>

        <Form.Select
          size="sm"
          className="w-auto flex-shrink-0"
          style={{ minWidth: 130 }}
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          aria-label="مرتب‌سازی"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>

        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
          <Button
            size="sm"
            variant="outline-primary"
            className="d-inline-flex align-items-center gap-2"
            disabled={syncing}
            onClick={onSyncSitemap}
          >
            {syncing ? (
              <>
                <Spinner
                  size="sm"
                  animation="border"
                  style={{ width: 12, height: 12 }}
                />
                همگام‌سازی…
              </>
            ) : (
              <>
                <IndexerIcon name="arrow-repeat" />
                همگام‌سازی sitemap
              </>
            )}
          </Button>

          <Dropdown align="start">
            <Dropdown.Toggle
              size="sm"
              variant="outline-secondary"
              disabled={busy}
              id="url-list-bulk-actions"
              className="d-inline-flex align-items-center gap-1"
            >
              عملیات
              {selectedCount > 0 ? (
                <Badge bg="primary" pill>
                  {formatFaNumber(selectedCount)}
                </Badge>
              ) : null}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>روی انتخاب‌شده‌ها</Dropdown.Header>
              <Dropdown.Item
                disabled={selectedCount === 0}
                onClick={onSendSelectedIndex}
              >
                درخواست ایندکس
              </Dropdown.Item>
              <Dropdown.Item
                disabled={selectedCount === 0 || busy}
                onClick={onInspectSelected}
              >
                بازرسی
              </Dropdown.Item>
              <Dropdown.Item
                disabled={selectedCount === 0}
                onClick={onCopySelected}
              >
                کپی
              </Dropdown.Item>
              <Dropdown.Item onClick={onExportTxt}>
                خروجی TXT (صفحه / انتخاب)
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Header>روی صفحهٔ فعلی</Dropdown.Header>
              <Dropdown.Item onClick={onSendNotIndexedPage}>
                ارسال ایندکس‌نیست‌ها برای ایندکس
              </Dropdown.Item>
              <Dropdown.Item onClick={onInspectAllPage}>
                بازرسی همه
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
